<template>
  <div>
    <ProgressBar
      v-if="!isLiteView()"
      active-step="Tell us about yourself"
    />
    <PageHeading
      default-heading="Next, tell us about your role"
      edit-heading="Tell us about your role"
    />
    <form
      novalidate="novalidate"
      @submit.prevent
    >
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          What's your role in the trust?
        </h2>
        <div class="pvd-form-row">
          <div class="form-row-label form-row-label-top label-icon">
            <label id="trust-role--main--label">
              <a
                id="trust-role-modal-trigger"
                href="javascript:void(0);"
              >
                <pvd-icon
                  icon="help"
                  class="m-small-left"
                />
              </a>Role in trust</label>
          </div>
          <pvd-modal
            id="trust-role-modal"
            for="trust-role-modal-trigger"
            class="small"
          >
            <div slot="modal-header">
              <h2 class="pvd-heading-L">
                Trust roles
              </h2>
            </div>
            <div slot="modal-body">
              <div class="pvd-container">
                <p>
                  <strong>Trustee</strong><br>
                  The trustee is a person or institution who has control of a trust’s assets and has a fiduciary
                  responsibility to appropriately handle the assets. A Fidelity trust account needs at least one
                  trustee, with a maximum number of 4.
                </p>
                <p>
                  <strong>Grantor</strong><br>
                  A grantor is the person who creates or contributes the assets to a trust. A Fidelity trust account
                  needs at least one grantor, with a maximum of 6.
                </p>
              </div>
            </div>
          </pvd-modal>
          <div class="form-row-content">
            <fieldset aria-labelledby="trust-role--main--label">
              <div class="pvd-radio-btn">
                <input
                  id="trust-role--main--trustee"
                  v-model="trustRoleValue"
                  v-validate="{required: true}"
                  value="trustee"
                  type="radio"
                  name="trust-role--main"
                >
                <label for="trust-role--main--trustee">Trustee</label>
              </div>
              <div class="pvd-radio-btn">
                <input
                  id="trust-role--main--trustee-grantor"
                  v-model="trustRoleValue"
                  value="trustee/grantor"
                  type="radio"
                  name="trust-role--main"
                >
                <label for="trust-role--main--trustee-grantor">Trustee and grantor</label>
              </div>
            </fieldset>
          </div>
          <InlineMessage
            v-if="errors.has('trust-role--main')"
            message="Please make a selection."
            :css-class="'form-row-message p-small-top'"
          />
          <div
            v-if="hasMaxGrantors"
            class="form-row-message"
          >
            <pvd-system-message
              placement="inline"
              type="alert"
              message="You’ve reached the limit of 6 grantors for this account. If you also want to add yourself as a grantor,
              you'll have to delete someone else first."
            />
          </div>
        </div>
      </div>
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          Select any authorities granted to you by the trust agreement
        </h2>
        <div class="pvd-form-row">
          <div class="form-row-label form-row-label-top label-icon">
            <label id="trust-authorities--main--label"> <a
              id="trust-authorities-modal-trigger"
              href="javascript:void(0);"
            >
              <pvd-icon
                icon="help"
                class="m-small-left"
              />
            </a>Authorities</label>
          </div>
          <pvd-modal
            id="trust-authorities-modal"
            for="trust-authorities-modal-trigger"
            class="small"
          >
            <div slot="modal-header">
              <h2 class="pvd-heading-L">
                Trust authorities
              </h2>
            </div>
            <div slot="modal-body">
              <div class="pvd-container">
                <p>
                  Trust authorities include the power to amend conditions of the trust,
                  revoke conditions of the trust, and the power to appoint or remove
                  individual trustees. The people who may hold these authorities can
                  vary by type of trust and the specific trust terms.
                  <br>
                  <br>
                  <strong>Refer to your trust documents</strong>
                  for this information when answering this question.
                </p>
              </div>
            </div>
          </pvd-modal>
          <div class="form-row-content">
            <fieldset aria-labelledby="trust-authorities--main--label">
              <div class="pvd-checkbox">
                <input
                  id="trust-authorities--main--amend"
                  v-model="amendValue"
                  type="checkbox"
                  name="trust-authorities--main"
                  value="true"
                >
                <label for="trust-authorities--main--amend">
                  <span
                    class="checkmark"
                    aria-hidden="true"
                  >
                    <pvd-icon icon="checkmark" />
                  </span>Can amend conditions of the trust
                </label>
              </div>
              <div
                v-if="acc_details.isIrrevocable === false"
                class="pvd-checkbox"
              >
                <input
                  id="trust-authorities--main--revoke"
                  v-model="revokeValue"
                  type="checkbox"
                  name="trust-authorities--main"
                  value="true"
                >
                <label for="trust-authorities--main--revoke">
                  <span
                    class="checkmark"
                    aria-hidden="true"
                  >
                    <pvd-icon icon="checkmark" />
                  </span>Can revoke conditions of the trust
                </label>
              </div>
              <div class="pvd-checkbox">
                <input
                  id="trust-authorities--main--appoint-remove"
                  v-model="appointRemoveValue"
                  type="checkbox"
                  name="trust-authorities--main"
                  value="true"
                >
                <label for="trust-authorities--main--appoint-remove">
                  <span
                    class="checkmark"
                    aria-hidden="true"
                  >
                    <pvd-icon icon="checkmark" />
                  </span>Can appoint/remove trustees
                </label>
              </div>
            </fieldset>
          </div>
          <div
            v-if="maxAuthoritiesError"
            class="form-row-message"
          >
            <pvd-system-message
              placement="inline"
              type="alert"
              :message="maxAuthoritiesError"
            />
          </div>
        </div>
      </div>

      <PageNavigation
        primary-button-type="button"
        :show-primary-button-spinner="shouldShowLoadingSpinner"
        :disable-primary-button="shouldDisableNext"
        :primary-button-label="primaryButtonLabel"
        :secondary-button-label="pd_content.secondary_button.default"
        :page-exit-url="pd_content.overlay_exit_link.overview_url"
        @primary-button-clicked="nextButtonClicked"
      />
    </form>
    <div
      id="fs-steps"
      class="personal-info-page"
      data-operation="IdentifyUser"
      data-step="Init"
      style="display: none"
    >
      &nbsp;
    </div>
  </div>
</template>

<script>
import ProgressBar from '../components/ProgressBar.vue';
import PageNavigation from '../components/PageNavigation.vue';
import PageHeading from '../components/PageHeading.vue';
import dataHelper from '../mixins/dataHelper';
import dataSanitize from '../mixins/dataSanitize';
import measurement from '../mixins/measurement';
import utilityFunctions from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import widgetHelper from '../mixins/widgetHelper';
import pageNavigationHelper from '../mixins/pageNavigationHelper';
import InlineMessage from '../components/InlineMessage.vue';

export default {
  name: 'TrustRole',
  components: {
    ProgressBar,
    PageNavigation,
    PageHeading,
    InlineMessage,
  },
  mixins: [
    dataHelper,
    dataSanitize,
    measurement,
    utilityFunctions,
    validationHelper,
    widgetHelper,
    pageNavigationHelper,
  ],
  data() {
    return {
      trustRole: '',
      hasAppointRemove: false,
      hasAmendRevoke: false,
      hasAmend: false,
      hasRevoke: false,
      backButtonAttempt: false,
      MAX_GRANTORS: 6,
      MAX_APPOINT_REMOVE: 6,
      MAX_AMEND_REVOKE: 6,
      additionalRoles: [],
      isLoading: false,
    };
  },
  computed: {
    shouldDisableNext() {
      return this.isLoading ? 'Y' : 'N';
    },
    shouldShowLoadingSpinner() {
      return this.isLoading ? 'Y' : 'N';
    },
    primaryButtonLabel() {
      return this.isLoading ? this.pd_content.primary_button.wait : this.pd_content.primary_button.default;
    },
    trustRoleValue: {
      get() {
        return this.trustRole;
      },
      set(newTrustRole) {
        this.trustRole = newTrustRole;
        this.commitPrimaryUserData(this.trustRole, 'additionalDetails.role');
      },
    },
    appointRemoveValue: {
      get() {
        return this.hasAppointRemove;
      },
      set(newHasAppointRemove) {
        this.hasAppointRemove = newHasAppointRemove;
        this.commitPrimaryUserData(this.hasAppointRemove, 'additionalDetails.hasAppointRemove');
      },
    },
    amendRevokeValue: {
      get() {
        return this.hasAmendRevoke;
      },
      set(newHasAmendRevoke) {
        this.hasAmendRevoke = newHasAmendRevoke;
        this.commitPrimaryUserData(this.hasAmendRevoke, 'additionalDetails.hasAmendRevoke');
      },
    },
    amendValue: {
      get() {
        return this.hasAmend;
      },
      set(newHasAmend) {
        this.hasAmend = newHasAmend;
        this.amendRevokeValue = (this.hasAmend || this.hasRevoke);
        this.commitPrimaryUserData(this.hasAmend, 'additionalDetails.hasAmend');
      },
    },
    revokeValue: {
      get() {
        return this.hasRevoke;
      },
      set(newHasRevoke) {
        this.hasRevoke = newHasRevoke;
        this.amendRevokeValue = (this.hasRevoke || this.hasAmend);
        this.commitPrimaryUserData(this.hasRevoke, 'additionalDetails.hasRevoke');
      },
    },
    hasMaxGrantors: {
      get() {
        return this.trustRole === 'trustee/grantor' && this.additionalRoles.filter((user) => user.role === 'trustee/grantor' || user.role === 'grantor').length === this.MAX_GRANTORS;
      },
    },
    hasMaxAmend: {
      get() {
        return this.hasAmendRevoke && this.additionalRoles.filter((user) => user.hasAmendRevoke).length === this.MAX_AMEND_REVOKE;
      },
    },
    hasMaxAppoint: {
      get() {
        return this.hasAppointRemove && this.additionalRoles.filter((user) => user.hasAppointRemove).length === this.MAX_APPOINT_REMOVE;
      },
    },
    maxAuthoritiesError: {
      get() {
        if (this.hasMaxAmend && this.hasMaxAppoint) {
          return "You’ve reached the limits of 6 people with the authority to amend/revoke conditions of trust and 6 people with the the authority to appoint/remove trustees. To replace a person with these authorities, you'll have to edit one you previously added.";
        }
        if (this.hasMaxAmend) {
          return "You've reached the limit of 6 people with amend/revoke authority. To replace a person with this authority, you'll have to edit one you previously added.";
        }
        if (this.hasMaxAppoint) {
          return "You've reached the limit of 6 people with appoint/remove authority. To replace a person with this authority, you'll have to edit one you previously added.";
        }
        return '';
      },
    },
  },
  mounted() {
    this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));
    this.removeModalClassIfExists();
    this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));

    document.title = this.setTitle('Tell us about your role');

    this.additionalRoles = this.getInstanceData().formData.additionalUsers;

    if (this.pu_additionalDetails && this.pu_additionalDetails.role) { this.trustRole = this.pu_additionalDetails.role; }

    if (this.pu_additionalDetails && this.pu_additionalDetails.hasAppointRemove) {
      this.hasAppointRemove = true;
    } else {
      this.commitPrimaryUserData(false, 'additionalDetails.hasAppointRemove');
    }

    if (this.pu_additionalDetails) {
      this.amendValue = !!this.pu_additionalDetails.hasAmend;
      this.revokeValue = !!this.pu_additionalDetails.hasRevoke;
    }

    this.isLoading = false;
  },

  methods: {
    nextButtonClicked(navAction) {
      this.isLoading = true;
      this.performPageValidation(navAction).then((valid) => {
        if (valid && !this.hasMaxGrantors && !this.hasMaxAppoint && !this.hasMaxAmend) {
          this.performNavigation(this.pd_content.primary_button.value);
        } else {
          this.isLoading = false;
        }
      });
    },
  },
};

</script>

<style scoped>
  .top-margin-zero {
    margin-top: 0;
  }
</style>
